import { useEffect } from 'react';

import { store } from 'store';
import { getErc20Abi } from 'utils/helpers';
import { Address, ContractFunctionParameters, zeroAddress } from 'viem';
import { useBalance, useReadContracts } from 'wagmi';

type Props = {
  chainId: number | undefined;
  tokens: Address[];
  refetchIntervalSec?: number;
  enabled?: boolean;
};

export const useBalances = (props: Props) => {
  const { chainId, tokens, refetchIntervalSec, enabled } = props;
  const { account, addBalances } = store.web3Store;
  const refetchInterval = refetchIntervalSec && refetchIntervalSec * 1000;

  const nativeBalanceResult = useBalance({
    address: account,
    chainId,
    query: {
      refetchInterval,
      enabled: enabled && !!chainId && (tokens.length > 2 || tokens.includes(zeroAddress)),
    },
  });

  const contracts: ContractFunctionParameters[] =
    account && chainId
      ? tokens
          .filter((t) => t !== zeroAddress)
          .map((t) => ({
            address: t,
            abi: getErc20Abi(chainId, t),
            functionName: 'balanceOf',
            args: [account],
            chainId,
          }))
      : [];

  const balancesResults = useReadContracts({
    contracts,
    query: {
      refetchInterval,
      enabled,
    },
  });

  useEffect(() => {
    if (!chainId) return;
    if (nativeBalanceResult.status === 'success') {
      addBalances(chainId, { [zeroAddress]: String(nativeBalanceResult.data.value) });
    } else if (nativeBalanceResult.error) {
      console.error('Native token balance error:', nativeBalanceResult.error.message);
      addBalances(chainId, { [zeroAddress]: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeBalanceResult.data]);

  useEffect(() => {
    if (!chainId) return;
    const balances: Record<Address, string | undefined> = {};

    balancesResults.data?.forEach((res, i) => {
      const address = contracts[i].address;
      if (res.status === 'success') {
        balances[address] = String(res.result);
      } else {
        if (res.error) {
          console.error(`Token ${address} balance error:`, res.error.message);
        }
        balances[address] = undefined;
      }
    });

    addBalances(chainId, balances);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balancesResults.data]);
};
